/**
 * Custos operacionais da conta administradora. Exige TOTP step-up.
 */

import { api } from '@/lib/api/client'
import type {
  OperatingCostCategory,
  OperatingCostCreditCampaignFees,
  OperatingCostCreditFeesPage,
  OperatingCostCreditInstallment,
  OperatingCostEntry,
  OperatingCostKind,
  OperatingCostMonthlyEntry,
  OperatingCostPaymentFeePostingsPage,
  OperatingCostPixFee,
  OperatingCostPixFeesPage,
  OperatingCostSalePostingsPage,
  OperatingCostSplitPostingsPage,
  OperatingCostValueType,
  OperatingCostsSnapshot,
  OperatingProfitSplitPostingsPage,
} from '@/types/api'

export type CreateOperatingCostCategoryInput = {
  name: string
  kind: OperatingCostKind
  valueType: OperatingCostValueType
}

export type CreateOperatingCostEntryInput = {
  subtitle: string
  amount?: number | null
  rate?: number | null
  competenceMonth?: string | null
  competenceMonthEnd?: string | null
}

export type CreateOperatingCostMonthlyEntryInput = {
  year: number
  month: number
  amount: number
}

export async function fetchOperatingCosts(
  signal?: AbortSignal
): Promise<OperatingCostsSnapshot> {
  return api.get<OperatingCostsSnapshot>('/api/admin/operating-costs', {
    requireAuth: true,
    signal,
  })
}

export async function createOperatingCostCategory(
  input: CreateOperatingCostCategoryInput
): Promise<OperatingCostCategory> {
  const response = await api.post<{ category: OperatingCostCategory }>(
    '/api/admin/operating-costs/categories',
    {
      name: input.name,
      kind: input.kind,
      value_type: input.valueType,
    },
    { requireAuth: true }
  )
  return response.category
}

export async function renameOperatingCostCategory(
  categoryId: number,
  name: string
): Promise<OperatingCostCategory> {
  const response = await api.patch<{ category: OperatingCostCategory }>(
    `/api/admin/operating-costs/categories/${categoryId}`,
    { name },
    { requireAuth: true }
  )
  return response.category
}

export async function deleteOperatingCostCategory(categoryId: number): Promise<void> {
  await api.delete(`/api/admin/operating-costs/categories/${categoryId}`, {
    requireAuth: true,
  })
}

export async function createOperatingCostEntry(
  categoryId: number,
  input: CreateOperatingCostEntryInput
): Promise<OperatingCostEntry> {
  const response = await api.post<{ entry: OperatingCostEntry }>(
    `/api/admin/operating-costs/categories/${categoryId}/entries${input.competenceMonthEnd ? '/batch' : ''}`,
    {
      subtitle: input.subtitle,
      amount: input.amount ?? null,
      rate: input.rate ?? null,
      ...(input.competenceMonth !== undefined
        ? { competence_month: input.competenceMonth }
        : {}),
      ...(input.competenceMonthEnd !== undefined
        ? { competence_month_end: input.competenceMonthEnd }
        : {}),
    },
    { requireAuth: true }
  )
  return response.entry
}

export async function updateOperatingCostEntry(
  entryId: number,
  input: CreateOperatingCostEntryInput
): Promise<OperatingCostEntry> {
  const response = await api.patch<{ entry: OperatingCostEntry }>(
    `/api/admin/operating-costs/entries/${entryId}`,
    {
      subtitle: input.subtitle,
      amount: input.amount ?? null,
      rate: input.rate ?? null,
      ...(input.competenceMonth !== undefined
        ? { competence_month: input.competenceMonth }
        : {}),
    },
    { requireAuth: true }
  )
  return response.entry
}

export async function deleteOperatingCostEntry(entryId: number): Promise<void> {
  await api.delete(`/api/admin/operating-costs/entries/${entryId}`, {
    requireAuth: true,
  })
}

export async function createOperatingCostMonthlyEntry(
  categoryId: number,
  input: CreateOperatingCostMonthlyEntryInput
): Promise<OperatingCostMonthlyEntry> {
  const response = await api.post<{ monthly_entry: OperatingCostMonthlyEntry }>(
    `/api/admin/operating-costs/categories/${categoryId}/monthly-entries`,
    {
      year: input.year,
      month: input.month,
      amount: input.amount,
    },
    { requireAuth: true }
  )
  return response.monthly_entry
}

export async function updateOperatingCostMonthlyEntry(
  entryId: number,
  input: CreateOperatingCostMonthlyEntryInput
): Promise<OperatingCostMonthlyEntry> {
  const response = await api.patch<{ monthly_entry: OperatingCostMonthlyEntry }>(
    `/api/admin/operating-costs/monthly-entries/${entryId}`,
    {
      year: input.year,
      month: input.month,
      amount: input.amount,
    },
    { requireAuth: true }
  )
  return response.monthly_entry
}

export async function deleteOperatingCostMonthlyEntry(entryId: number): Promise<void> {
  await api.delete(`/api/admin/operating-costs/monthly-entries/${entryId}`, {
    requireAuth: true,
  })
}

export async function updateOperatingCostUnitRate(
  categoryId: number,
  amount: number
): Promise<OperatingCostCategory> {
  const response = await api.patch<{ category: OperatingCostCategory }>(
    `/api/admin/operating-costs/categories/${categoryId}/unit-rate`,
    { amount },
    { requireAuth: true }
  )
  return response.category
}

export type FetchOperatingCostSalePostingsParams = {
  campaignId?: number | null
  month?: string
  from?: string
  to?: string
  page?: number
  perPage?: number
}

export async function fetchOperatingCostSalePostings(
  categoryId: number,
  params: FetchOperatingCostSalePostingsParams = {},
  signal?: AbortSignal
): Promise<OperatingCostSalePostingsPage> {
  const query = new URLSearchParams()

  if (params.campaignId && params.campaignId > 0) {
    query.set('campaign_id', String(params.campaignId))
  }
  if (params.month?.trim()) {
    query.set('month', params.month.trim())
  }
  if (params.from?.trim()) {
    query.set('from', params.from.trim())
  }
  if (params.to?.trim()) {
    query.set('to', params.to.trim())
  }
  if (params.page && params.page > 1) {
    query.set('page', String(params.page))
  }
  if (params.perPage && params.perPage > 0) {
    query.set('per_page', String(params.perPage))
  }

  const suffix = query.toString()
  const path = suffix
    ? `/api/admin/operating-costs/categories/${categoryId}/sale-postings?${suffix}`
    : `/api/admin/operating-costs/categories/${categoryId}/sale-postings`

  return api.get<OperatingCostSalePostingsPage>(path, {
    requireAuth: true,
    signal,
  })
}

export type FetchOperatingCostSplitPostingsParams =
  FetchOperatingCostSalePostingsParams & {
    institutionId?: number | null
  }

export type FetchOperatingProfitSplitPostingsParams =
  FetchOperatingCostSplitPostingsParams

function splitPostingsQuery(params: FetchOperatingCostSplitPostingsParams): string {
  const query = new URLSearchParams()

  if (params.campaignId && params.campaignId > 0) {
    query.set('campaign_id', String(params.campaignId))
  }
  if (params.institutionId && params.institutionId > 0) {
    query.set('institution_id', String(params.institutionId))
  }
  if (params.month?.trim()) {
    query.set('month', params.month.trim())
  }
  if (params.from?.trim()) {
    query.set('from', params.from.trim())
  }
  if (params.to?.trim()) {
    query.set('to', params.to.trim())
  }
  if (params.page && params.page > 1) {
    query.set('page', String(params.page))
  }
  if (params.perPage && params.perPage > 0) {
    query.set('per_page', String(params.perPage))
  }

  return query.toString()
}

export async function fetchOperatingCostSplitPostings(
  categoryId: number,
  params: FetchOperatingCostSplitPostingsParams = {},
  signal?: AbortSignal
): Promise<OperatingCostSplitPostingsPage> {
  const suffix = splitPostingsQuery(params)
  const path = suffix
    ? `/api/admin/operating-costs/categories/${categoryId}/split-postings?${suffix}`
    : `/api/admin/operating-costs/categories/${categoryId}/split-postings`

  return api.get<OperatingCostSplitPostingsPage>(path, {
    requireAuth: true,
    signal,
  })
}

export async function fetchOperatingProfitSplitPostings(
  params: FetchOperatingProfitSplitPostingsParams = {},
  signal?: AbortSignal
): Promise<OperatingProfitSplitPostingsPage> {
  const suffix = splitPostingsQuery(params)
  const path = suffix
    ? `/api/admin/operating-costs/profit-split-postings?${suffix}`
    : '/api/admin/operating-costs/profit-split-postings'

  return api.get<OperatingProfitSplitPostingsPage>(path, {
    requireAuth: true,
    signal,
  })
}

export type FetchOperatingCostPaymentFeePostingsParams =
  FetchOperatingCostSplitPostingsParams

export async function fetchOperatingCostPaymentFeePostings(
  categoryId: number,
  params: FetchOperatingCostPaymentFeePostingsParams = {},
  signal?: AbortSignal
): Promise<OperatingCostPaymentFeePostingsPage> {
  const suffix = splitPostingsQuery(params)
  const path = suffix
    ? `/api/admin/operating-costs/categories/${categoryId}/payment-fee-postings?${suffix}`
    : `/api/admin/operating-costs/categories/${categoryId}/payment-fee-postings`

  return api.get<OperatingCostPaymentFeePostingsPage>(path, {
    requireAuth: true,
    signal,
  })
}

export type UpsertOperatingCostPixFeeInput = {
  rate: number
  fixedAmount?: number
}

export async function fetchOperatingCostPixFees(
  signal?: AbortSignal
): Promise<OperatingCostPixFeesPage> {
  return api.get<OperatingCostPixFeesPage>('/api/admin/operating-costs/pix-fees', {
    requireAuth: true,
    signal,
  })
}

export async function upsertOperatingCostDefaultPixFee(
  input: UpsertOperatingCostPixFeeInput
): Promise<OperatingCostPixFee> {
  const response = await api.put<{ fee: OperatingCostPixFee }>(
    '/api/admin/operating-costs/pix-fees/default',
    {
      rate: input.rate,
      fixed_amount: input.fixedAmount ?? 0,
    },
    { requireAuth: true }
  )
  return response.fee
}

export async function deleteOperatingCostDefaultPixFee(): Promise<void> {
  await api.delete('/api/admin/operating-costs/pix-fees/default', { requireAuth: true })
}

export async function upsertOperatingCostCampaignPixFee(
  campaignId: number,
  input: UpsertOperatingCostPixFeeInput
): Promise<OperatingCostPixFee> {
  const response = await api.put<{ fee: OperatingCostPixFee }>(
    `/api/admin/operating-costs/pix-fees/campaigns/${campaignId}`,
    {
      rate: input.rate,
      fixed_amount: input.fixedAmount ?? 0,
    },
    { requireAuth: true }
  )
  return response.fee
}

export async function deleteOperatingCostCampaignPixFee(
  campaignId: number
): Promise<void> {
  await api.delete(`/api/admin/operating-costs/pix-fees/campaigns/${campaignId}`, {
    requireAuth: true,
  })
}

export type UpsertOperatingCostCreditInstallmentInput = {
  installment: number
  rate: number
  surchargeRate?: number
  surchargeAmount?: number
}

export async function fetchOperatingCostCreditFees(
  signal?: AbortSignal
): Promise<OperatingCostCreditFeesPage> {
  return api.get<OperatingCostCreditFeesPage>('/api/admin/operating-costs/credit-fees', {
    requireAuth: true,
    signal,
  })
}

export async function upsertOperatingCostDefaultCreditFees(
  installments: UpsertOperatingCostCreditInstallmentInput[]
): Promise<{ installments: OperatingCostCreditInstallment[] }> {
  return api.put(
    '/api/admin/operating-costs/credit-fees/default',
    {
      installments: installments.map((item) => ({
        installment: item.installment,
        rate: item.rate,
        surcharge_rate: item.surchargeRate ?? 0,
        surcharge_amount: item.surchargeAmount ?? 0,
      })),
    },
    { requireAuth: true }
  )
}

export async function deleteOperatingCostDefaultCreditFees(): Promise<void> {
  await api.delete('/api/admin/operating-costs/credit-fees/default', {
    requireAuth: true,
  })
}

export async function upsertOperatingCostCampaignCreditFees(
  campaignId: number,
  installments: UpsertOperatingCostCreditInstallmentInput[]
): Promise<OperatingCostCreditCampaignFees> {
  return api.put(
    `/api/admin/operating-costs/credit-fees/campaigns/${campaignId}`,
    {
      installments: installments.map((item) => ({
        installment: item.installment,
        rate: item.rate,
        surcharge_rate: item.surchargeRate ?? 0,
        surcharge_amount: item.surchargeAmount ?? 0,
      })),
    },
    { requireAuth: true }
  )
}

export async function deleteOperatingCostCampaignCreditFees(
  campaignId: number
): Promise<void> {
  await api.delete(`/api/admin/operating-costs/credit-fees/campaigns/${campaignId}`, {
    requireAuth: true,
  })
}
